import {
    useCallback, useEffect, useMemo, useState
} from 'react'
import { isEqual } from 'lodash'
import { ASTNode } from 'graphql/language/ast'

import { useQuery, usePrevious } from 'hooks'
import { Pagination } from 'generated/graphql'
import { QueryResponse } from 'utils/graphql'

type usePaginationProps<T, V> = {
  variables: V & { first?: number },
  query: ASTNode|((variables?: V) => Promise<QueryResponse<T>>),
  initPage: number,
  initItemsPerPage: number,
  getList: (data: T) => any,
  getPageInfo: (data: T) => Pagination
  getTotalCount: (data: T) => number
}

/**
 * A hook to wrap {@code useQuery} for the given graphql query. It would inject the params for pagination to the
 * query variables and provide handlers for {@code <Pagination/>} component.
 *
 * @param variables variables of graphql query
 * @param query graphql query
 * @param initPage initial page to load
 * @param initItemsPerPage initial items per page to load
 * @param getList func to get the list of items from graphql data response
 * @param getPageInfo func to get the page info from graphql data response
 * @param getTotalCount func to get the total number of items from graphql data response
 *
 * @return a object of {
 *   requestStatus: current request status
 *   list: list of retrieved items
 *   fetchData: func to fetch data (execute graphql query on demand)
 *   handlePageChange: func to handle on change page of {@code <Pagination/>} component
 *   pagination: current pagination info (page and items per page)
 *   totalItems: total number of fetched items
 * }
 */
const usePagination = <T, V>({
    variables,
    query,
    initPage,
    initItemsPerPage,
    getList,
    getPageInfo,
    getTotalCount,
}: usePaginationProps<T, V>) => {
    const [pagination, setPagination] = useState({
        page: initPage,
        itemsPerPage: initItemsPerPage
    })
    const [pageCursors, setPageCursors] = useState<string[]>([undefined])
    const pagingQueryVars = useMemo(() => ({
        first: pagination.itemsPerPage,
        after: pageCursors[pagination.page - 1]
    }), [pageCursors, pagination.itemsPerPage, pagination.page])

    // Callback for {@code <Pagination/>} component usage
    const handlePageChange = useCallback(async ({ page, itemsPerPage }) => {
        setPagination({
            page,
            itemsPerPage,
        })
    }, [setPagination])

    const {
        data,
        requestStatus,
        pageInfo,
        fetchData,
    } = useQuery<T, V>(
    query as ASTNode,
    { ...variables, ...pagingQueryVars },
    (_data: T) => getPageInfo(_data)
    )

    const list = useMemo(() => getList(data), [data, getList])

    const previousVariables = usePrevious(variables)
    // Reset pagination if variables have been changed (probably change filters or sorting). Set page to 1.
    useEffect(() => {
        if (!isEqual(variables, previousVariables)) {
            setPageCursors([undefined])
            setPagination({ page: 1, itemsPerPage: pagination.itemsPerPage })
        }
    }, [pagination.itemsPerPage, variables, previousVariables])

    // Build cursors list for further pagination query loads. Or memorize the returned cursor, this is why the pagination
    // is linear (Cannot navigate directly to a specific page but from page 1).
    useEffect(() => {
        const cursors = [...pageCursors]
        cursors.splice(pagination.page, 1, pageInfo?.endCursor)
        if (!isEqual(cursors, pageCursors)) {
            setPageCursors(cursors)
        }
    }, [pagination.page, pageInfo?.endCursor, pageCursors])

    return {
        requestStatus,
        list,
        fetchData,
        handlePageChange,
        pagination,
        totalItems: getTotalCount(data) || 0,
    }
}

export default usePagination
